import { useState } from 'react';
import { startCabsSignIn } from '../lib/cabs.ts';
import { Link, useNavigate } from 'react-router';
import { ApiRefusal } from '@gatecheckpro/api-client';
import { Button, Card, Field, Notice } from '@gatecheckpro/design-system';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { FailureNotice } from '../lib/refusal.tsx';

/**
 * Sign in.
 *
 * The second factor is a *second step*, not a field shown speculatively. The
 * server answers `AUTH_MFA_REQUIRED` once the password is known-good, which is
 * the only point at which asking for a code is meaningful — and which keeps the
 * form from telling an attacker that an account has MFA before they have proved
 * the password.
 */
export function SignInPage() {
  const navigate = useNavigate();
  const { reload } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [needsSecondFactor, setNeedsSecondFactor] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [cabsBusy, setCabsBusy] = useState(false);

  /*
   * Its own action, and its own ceremony.
   *
   * A CABS identity has no password here, so typing one into the form above cannot work. This
   * starts the ceremony on this origin, against this origin's API, and the session it produces is
   * stored by this console alone. There is deliberately no first-use action: accounts are created
   * on the attendee surface, and authenticating at CABS grants no access here.
   */
  async function signInWithCabs(): Promise<void> {
    setCabsBusy(true);
    setError(null);
    try {
      await startCabsSignIn('/organizations');
    } catch (caught) {
      setError(caught);
      setCabsBusy(false);
    }
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await api.request<{
        accessToken: string;
        accessTokenExpiresAt: string;
        sessionToken: string;
        sessionExpiresAt: string;
        mfaSatisfied: boolean;
      }>('/api/v1/auth/login', {
        method: 'POST',
        anonymous: true,
        body: {
          email,
          password,
          ...(needsSecondFactor && mfaCode.length > 0 ? { mfaCode } : {}),
          deviceName: 'Organizer web',
        },
      });
      api.setSession(session);
      await reload();
      void navigate('/organizations', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiRefusal && caught.code === 'AUTH_MFA_REQUIRED') {
        setNeedsSecondFactor(true);
        setError(null);
      } else {
        setError(caught);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="gc-auth">
      <div className="gc-auth__card gc-stack">
        <div className="gc-stack gc-stack--tight">
          <span className="gc-wordmark">
            GateCheck<span className="gc-wordmark__accent">Pro</span>
          </span>
          <p className="gc-lede">The organizer workspace for ticketing and gate control.</p>
        </div>

        <Card>
          <form className="gc-stack" onSubmit={(event) => void submit(event)}>
            <h1>Sign in</h1>

            <FailureNotice error={error} />

            {needsSecondFactor && (
              <Notice tone="info" title="One more step">
                Enter the 6-digit code from your authenticator app, or a recovery code.
              </Notice>
            )}

            <Field
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Field
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />

            {needsSecondFactor && (
              <Field
                label="Authentication code"
                name="mfaCode"
                inputMode="text"
                autoComplete="one-time-code"
                autoFocus
                hint="6 digits from your app, or one of your recovery codes."
                value={mfaCode}
                onChange={(event) => setMfaCode(event.target.value)}
              />
            )}

            <Button type="submit" variant="primary" size="large" block busy={busy}>
              {needsSecondFactor ? 'Verify and sign in' : 'Sign in'}
            </Button>
          </form>
        </Card>

        <Card>
          <div className="gc-stack gc-stack--tight">
            <h2>Sign in with CABS</h2>
            <p className="gc-muted">
              If your organizer access is linked to a CABS identity, sign in with it rather than
              with a password. Your access here is unchanged by it: CABS proves who you are, and
              GateCheckPro still decides what you may do.
            </p>
            <Button
              type="button"
              variant="secondary"
              size="large"
              block
              busy={cabsBusy}
              onClick={() => void signInWithCabs()}
            >
              Sign in with CABS
            </Button>
          </div>
        </Card>
        <p className="gc-muted">
          New organizer? <Link to="/register">Create an account</Link>
        </p>
      </div>
    </main>
  );
}
